// src/components/FloatingVideoButton.tsx


import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { GET_PLATFORM_VIDEO_URL } from '../graphql/utils/queries';
import FloatingButtonIcon from './assets/images/Floating_Button_Icon.webp';
import { PauseOutlined, PlayCircleOutlined, CloseOutlined, FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons';

import { formatTime } from '../utils/formatTime';

interface FloatingVideoHelpButtonProps {
  filename: string;
  title: string;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  icon?: React.ReactNode;
  label?: string;
  className?: string;
}

const positionClasses: Record<string, string> = {
  'bottom-right': 'bottom-4 right-4',
  'bottom-left': 'bottom-4 left-4',
  'top-right': 'top-4 right-4',
  'top-left': 'top-4 left-4',
};

export const FloatingVideoHelpButton: React.FC<FloatingVideoHelpButtonProps> = ({
  filename,
  title,
  position = 'bottom-right',
  icon = <img src={FloatingButtonIcon} alt="Help" className="w-6 h-6" />,
  label,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isInPictureInPicture, setIsInPictureInPicture] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(null);

  // Call the GraphQL query to get the video URL
  const { data: videoData, loading: videoLoading, error: videoError } = useQuery(GET_PLATFORM_VIDEO_URL, {
    variables: {
      filename: filename
    }
  });

  const toggleModal = () => {

    if (!isOpen && isInPictureInPicture && videoElement) {

      // If opening modal and video is in PiP mode, exit PiP
      if (document.pictureInPictureElement) {
        document.exitPictureInPicture();
      }
    }
    setIsOpen(prev => !prev);
  };

  // Use the fetched video URL if available, otherwise fall back to the prop
  const finalVideoLink = videoData?.getPlatformVideoUrl?.url;

  // Custom video controls
  const togglePlay = () => {

    if (videoElement) {
      if (isPlaying) {
        videoElement.pause();
      } else {
        videoElement.play();

      }
      setIsPlaying(!isPlaying);
    }
  };


  const toggleFullscreen = () => {
    if (videoElement) {
      if (!isFullscreen) {
        if (videoElement.requestFullscreen) {
          videoElement.requestFullscreen();
        } else if ((videoElement as any).webkitRequestFullscreen) {
          (videoElement as any).webkitRequestFullscreen();
        } else if ((videoElement as any).msRequestFullscreen) {
          (videoElement as any).msRequestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          (document as any).webkitExitFullscreen();
        } else if ((document as any).msExitFullscreen) {
          (document as any).msExitFullscreen();
        }
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoElement) {
      setCurrentTime(videoElement.currentTime);

    }
  };

  const handleLoadedMetadata = () => {

    if (videoElement) {
      setDuration(videoElement.duration);

    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {

    if (videoElement) {
      const newTime = parseFloat(e.target.value);
      videoElement.currentTime = newTime;

      setCurrentTime(newTime);
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    return false;
  };


  const handleVideoRef = (element: HTMLVideoElement | null) => {
    setVideoElement(element);
  };

  // Handle PiP mode changes
  useEffect(() => {
    if (videoElement) {
      const handleEnterPiP = () => setIsInPictureInPicture(true);
      const handleLeavePiP = () => setIsInPictureInPicture(false);
      
      videoElement.addEventListener('enterpictureinpicture', handleEnterPiP);
      videoElement.addEventListener('leavepictureinpicture', handleLeavePiP);
      
      return () => {
        videoElement.removeEventListener('enterpictureinpicture', handleEnterPiP);
        videoElement.removeEventListener('leavepictureinpicture', handleLeavePiP);
      };
    }
  }, [videoElement]);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={toggleModal}
        title={label}
        className={`
          fixed z-50 p-4 rounded-full shadow-lg bg-blue-600 text-white hover:bg-blue-700 transition
          ${positionClasses[position]} ${className}
        `}
      >
        {icon}
      </button>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="relative bg-white rounded-md shadow-lg max-w-3xl w-full p-4">
            {/* Modal Header */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{title}</h2>
              <button
                onClick={toggleModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <CloseOutlined />
              </button>
            </div>

            {/* Video Player */}
            {videoLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-gray-500">Loading video...</div>
              </div>
            ) : videoError ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-red-500">Error loading video: {videoError.message}</div>
              </div>
            ) : (
              <div className="relative">
                <video

                  ref={handleVideoRef}
                  autoPlay
                  controlsList="disablePictureInPicture nodownload"

                  className="w-full max-h-[80vh] rounded"
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onContextMenu={handleContextMenu}
                  src={finalVideoLink}
                >
                  Your browser does not support the video tag.
                </video>
                
                {/* Custom Controls */}
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 p-2">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={togglePlay}
                      className="text-white hover:text-gray-300"
                    >
                      {isPlaying ? <PauseOutlined /> : <PlayCircleOutlined />}
                    </button>
                    
                    <input
                      type="range"
                      min="0"
                      max={duration || 0}
                      value={currentTime}
                      onChange={handleSeek}
                      className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                    />
                    
                    <span className="text-white text-sm">
                      {formatTime(currentTime)} / {formatTime(duration)}
                    </span>

                    <button
                      onClick={toggleFullscreen}
                      className="text-white hover:text-gray-300"
                    >
                      {isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                    </button>

                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
